import {
  detectCardType,
  formatCardNumberInput,
  buildCardNumberMaskDisplay,
} from '../../utils/cardBrand';
import CardBrandIcon from './CardBrandIcon';

/**
 * Card number field with live brand detection (Visa, Mastercard, Amex, Discover, Diners, JCB).
 * Brand icon on the left; once recognized, a zero-fill mask shows the remaining digits.
 */
export default function CardNumberInput({
  id,
  name = 'cardNumber',
  value = '',
  onChange,
  placeholder = '1234 5678 9012 3456',
  required = false,
  autoComplete = 'cc-number',
  className = '',
  inputClassName = '',
  brandPosition = 'left',
  showZeroMask = true,
  floatingLabel = '',
}) {
  const digits = String(value).replace(/\D/g, '');
  const brand = detectCardType(value);
  const showBrand = brand && brand !== 'unknown';
  const brandOnLeft = brandPosition === 'left';
  const hasTyped = digits.length > 0;
  const maskDisplay =
    showZeroMask && showBrand && hasTyped ? buildCardNumberMaskDisplay(digits, brand) : null;

  const handleChange = (e) => {
    const formatted = formatCardNumberInput(e.target.value);
    if (onChange) {
      onChange({
        ...e,
        target: { ...e.target, name, value: formatted },
      });
    }
  };

  const brandEl = brandOnLeft ? (
    <span
      className="card-number-input__brand card-number-input__brand--left"
      id={showBrand ? `${id}-brand` : undefined}
    >
      <CardBrandIcon type={showBrand ? brand : undefined} showDefault={!showBrand} />
    </span>
  ) : showBrand ? (
    <span className="card-number-input__brand card-number-input__brand--right" id={`${id}-brand`}>
      <CardBrandIcon type={brand} />
    </span>
  ) : null;

  return (
    <div
      className={`card-number-input-wrap${
        brandOnLeft || showBrand
          ? ` card-number-input-wrap--has-brand card-number-input-wrap--brand-${brandPosition}`
          : ''
      }${maskDisplay ? ' card-number-input-wrap--has-mask' : ''}${
        floatingLabel ? ' card-number-input-wrap--float-label' : ''
      } ${className}`.trim()}
    >
      {brandOnLeft ? brandEl : null}
      <div className="card-number-input-stack">
        {maskDisplay ? (
          <div className="card-number-input-mask" aria-hidden="true">
            <span className="card-number-input-mask__spacer">{value}</span>
            <span className="card-number-input-mask__suffix">
              {maskDisplay.slice(value.length)}
            </span>
          </div>
        ) : null}
        <input
          id={id}
          name={name}
          type="text"
          inputMode="numeric"
          autoComplete={autoComplete}
          value={value}
          onChange={handleChange}
          placeholder={floatingLabel ? ' ' : maskDisplay ? undefined : placeholder}
          required={required}
          aria-label={floatingLabel || undefined}
          className={`card-number-input ${maskDisplay ? 'card-number-input--with-mask' : ''} ${inputClassName}`.trim()}
          aria-describedby={showBrand ? `${id}-brand` : undefined}
        />
        {floatingLabel ? (
          <span className="card-float-label" aria-hidden="true">
            {floatingLabel}
          </span>
        ) : null}
      </div>
      {!brandOnLeft ? brandEl : null}
    </div>
  );
}
